import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
    X,
    Zap,
    BanknoteArrowUp,
    CircleCheck,
    Loader2,
    Gift,
    Upload,
    CreditCard,
    Image as ImageIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { ttAddToCart, ttInitiateCheckout, ttCompletePayment } from "@/lib/tiktok";
import { fbAddToCart, fbInitiateCheckout, fbPurchase, fbAdvancedMatch } from "@/lib/meta";
import { CITIES } from "@/constants/brand";
import { useStorefront } from "@/hooks/useStorefront";
import { DiscountCountdown } from "@/components/landing/DiscountCountdown";
import OrderSuccessModal from "@/components/landing/OrderSuccessModal";
import OnlinePayBanner from "@/components/landing/OnlinePayBanner";

export default function CheckoutModal({ product, open, onClose }) {
    const bundles = product?.bundles || [];
    const [bundleQty, setBundleQty] = useState(bundles[0]?.quantity || 1);
    const [form, setForm] = useState({
        customer_name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        notes: "",
    });
    const [marketingOptIn, setMarketingOptIn] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState("cod");
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const { settings, discountCode, setDiscountCode, discountExpiresAt } = useStorefront();
    const reportedRef = useRef(false);
    const submittedRef = useRef(false);
    const initiatedRef = useRef(false);

    const selected = useMemo(
        () => bundles.find((b) => b.quantity === bundleQty) || bundles[0],
        [bundleQty, bundles],
    );

    const validCode =
        discountCode &&
        settings?.exit_discount_code &&
        discountCode.toUpperCase() ===
            settings.exit_discount_code.toUpperCase();
    const discountPct = validCode ? settings.exit_discount_percent : 0;
    const discountAmount = selected
        ? Math.round(((selected.price || 0) * discountPct) / 100)
        : 0;
    const onlineMethods = { easypaisa: true, jazzcash: true, hbl: true };
    const isOnline = paymentMethod in onlineMethods;
    const onlineEligible =
        isOnline && !!screenshotUrl && settings?.online_payment_enabled;
    const onlineDiscountAmount = onlineEligible
        ? Number(settings?.online_payment_discount || 0)
        : 0;
    const deliveryFee = Number(settings?.delivery_fee || 0);
    const subtotalAfterDiscounts = selected
        ? Math.max(0, (selected.price || 0) - discountAmount - onlineDiscountAmount)
        : 0;
    const payableAmount = subtotalAfterDiscounts + deliveryFee;

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            reportedRef.current = false;
            submittedRef.current = false;
            initiatedRef.current = false;
            ttAddToCart(product, selected);
            fbAddToCart(product, selected);
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    // Report abandoned cart on close without submit if we captured ≥1 field.
    useEffect(() => {
        if (open) return;
        if (submittedRef.current || reportedRef.current) return;
        const hasAny =
            form.customer_name.trim() ||
            form.phone.trim() ||
            form.city ||
            form.email.trim();
        if (!hasAny) return;
        reportedRef.current = true;
        api.post("/abandoned-carts", {
            product_slug: product?.slug || "",
            customer_name: form.customer_name,
            phone: form.phone,
            city: form.city,
            bundle_label: selected?.label || "",
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleScreenshotUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            toast.error("Please upload an image file (JPG, PNG, WEBP).");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image is larger than 5 MB.");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/payment-screenshots", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setScreenshotUrl(data.url);
            toast.success("Screenshot uploaded. 100 PKR extra discount applied.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed. Please try again.",
            );
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!/^(03[0-9]{9})$/.test(form.phone)) {
            toast.error(
                "Please enter a valid Pakistani mobile number (03XXXXXXXXX).",
            );
            return;
        }
        if (!form.customer_name.trim() || !form.address.trim() || !form.city) {
            toast.error("Please fill all required fields.");
            return;
        }
        if (marketingOptIn && form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        if (isOnline && !screenshotUrl) {
            toast.error("Please upload your payment screenshot to confirm.");
            return;
        }
        setLoading(true);
        submittedRef.current = true;
        try {
            const { data } = await api.post("/orders", {
                product_slug: product.slug,
                bundle_quantity: bundleQty,
                customer_name: form.customer_name,
                phone: form.phone,
                address: form.address,
                city: form.city,
                notes: form.notes,
                email: form.email || null,
                marketing_opt_in: marketingOptIn && !!form.email,
                discount_code: validCode ? discountCode : null,
                payment_method: paymentMethod,
                payment_screenshot_url: isOnline ? screenshotUrl : null,
            });
            setOrder(data);
            ttCompletePayment(data, { email: form.email, phone: form.phone });
            fbPurchase(data, { email: form.email, phone: form.phone });
            setDiscountCode("");
            toast.success("Order placed successfully!");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not place the order. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    const closeAll = () => {
        setOrder(null);
        onClose?.();
    };

    if (!open || !bundles.length) return null;

    return createPortal(
        <>
            <div
                data-testid="checkout-modal"
                className="fixed inset-0 z-[60] bg-forest-900/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-float-up"
                onClick={onClose}
            >
                <div
                    className="relative bg-cream rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl max-h-[94vh] overflow-y-auto shadow-2xl border border-forest-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Sticky header */}
                    <div className="sticky top-0 z-10 bg-cream/95 backdrop-blur border-b border-forest-100 px-5 sm:px-7 py-4 flex items-center justify-between">
                        <div>
                            <span className="bg-forest-800 text-gold-400 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest inline-flex items-center gap-1.5 border border-gold-500/30">
                                <Zap className="w-3 h-3" /> 1-Minute Order
                            </span>
                            <h2 className="font-serif text-xl sm:text-2xl font-bold text-forest-800 mt-1 leading-tight">
                                Cash on Delivery Checkout
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="checkout-modal-close"
                            aria-label="Close"
                            className="w-10 h-10 rounded-full bg-forest-800 text-gold-400 flex items-center justify-center hover:bg-forest-900"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        onFocusCapture={(e) => {
                            if (initiatedRef.current || !["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.type === "radio") return;
                            initiatedRef.current = true;
                            ttInitiateCheckout(product, selected);
                            fbInitiateCheckout(product, selected);
                        }}
                        className="p-5 sm:p-7 space-y-8"
                        data-testid="checkout-modal-form"
                    >
                        {validCode && (
                            <div
                                data-testid="discount-banner"
                                className="bg-gold-500/15 border border-gold-500/40 text-forest-800 rounded-2xl p-3.5 flex items-center gap-3"
                            >
                                <div className="w-10 h-10 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center shrink-0">
                                    <Gift className="w-5 h-5" />
                                </div>
                                <div className="flex-1 text-sm">
                                    <div className="font-bold">
                                        {discountPct}% OFF applied with code{" "}
                                        <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-forest-200">
                                            {discountCode.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-xs text-forest-700/80">
                                        You save {discountAmount.toLocaleString()} PKR today.
                                    </div>
                                    <DiscountCountdown
                                        expiresAt={discountExpiresAt}
                                        testid="checkout-discount-countdown"
                                        className="mt-1 text-xs text-red-700"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setDiscountCode("")}
                                    className="text-xs text-forest-700/70 hover:text-forest-900 font-bold"
                                >
                                    Remove
                                </button>
                            </div>
                        )}

                        {/* Step 1: Bundle */}
                        <div className="space-y-3">
                            <h3 className="font-serif font-bold text-base text-forest-800 flex items-center gap-2.5 border-b border-forest-100 pb-2.5">
                                <span className="w-6 h-6 rounded-full bg-forest-800 text-gold-400 text-[10px] flex items-center justify-center font-sans font-bold">
                                    1
                                </span>
                                Select your bundle
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {bundles.map((b) => {
                                    const active = bundleQty === b.quantity;
                                    return (
                                        <label
                                            key={b.quantity}
                                            data-testid={`modal-bundle-${b.quantity}`}
                                            className={`cursor-pointer relative rounded-2xl p-3 flex flex-col justify-between transition-all border-2 ${
                                                active
                                                    ? "border-forest-700 bg-white shadow-md"
                                                    : "border-forest-100 hover:border-forest-400 bg-white"
                                            }`}
                                        >
                                            {b.is_popular && (
                                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gold-500 text-forest-900 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                                                    Most Popular
                                                </span>
                                            )}
                                            <input
                                                type="radio"
                                                name="bundle"
                                                value={b.quantity}
                                                checked={active}
                                                onChange={() =>
                                                    setBundleQty(b.quantity)
                                                }
                                                className="sr-only"
                                            />
                                            <div className="mt-1">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <span className="text-[9px] font-bold uppercase tracking-wider text-forest-700">
                                                        {b.label}
                                                    </span>
                                                    <span
                                                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                                            active
                                                                ? "border-forest-700 bg-forest-700 text-gold-400"
                                                                : "border-forest-200"
                                                        }`}
                                                    >
                                                        {active && (
                                                            <CircleCheck className="w-3 h-3 fill-gold-400 text-forest-700" />
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="font-bold text-forest-900 text-sm">
                                                    {b.label}
                                                </div>
                                                {b.bonus && (
                                                    <div className="text-[11px] text-forest-700 font-medium mt-0.5">
                                                        {b.bonus}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-3 pt-2 border-t border-forest-100 flex items-baseline justify-between">
                                                <span className="text-base font-bold text-forest-800">
                                                    {b.price.toLocaleString()} PKR
                                                </span>
                                                {b.original_price > b.price && (
                                                    <span className="text-[11px] text-forest-700/50 line-through">
                                                        {b.original_price.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Step 2: Address */}
                        <div className="space-y-3">
                            <h3 className="font-serif font-bold text-base text-forest-800 flex items-center gap-2.5 border-b border-forest-100 pb-2.5">
                                <span className="w-6 h-6 rounded-full bg-forest-800 text-gold-400 text-[10px] flex items-center justify-center font-sans font-bold">
                                    2
                                </span>
                                Delivery details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field
                                    label="Full Name"
                                    required
                                    name="customer_name"
                                    testid="modal-input-name"
                                    value={form.customer_name}
                                    onChange={handleChange}
                                    placeholder="e.g. Muhammad Ahmed"
                                />
                                <Field
                                    label="Mobile Number"
                                    required
                                    name="phone"
                                    testid="modal-input-phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    onBlur={() => fbAdvancedMatch({ email: form.email, phone: form.phone })}
                                    placeholder="03XXXXXXXXX"
                                    hint="Format: 03001234567"
                                    inputMode="numeric"
                                    maxLength={11}
                                />
                                <Field
                                    className="sm:col-span-2"
                                    label="Complete Street Address"
                                    required
                                    name="address"
                                    testid="modal-input-address"
                                    value={form.address}
                                    onChange={handleChange}
                                    placeholder="House No, Street Name, Area / Sector"
                                />
                                <div>
                                    <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                        City *
                                    </label>
                                    <select
                                        required
                                        name="city"
                                        data-testid="modal-input-city"
                                        value={form.city}
                                        onChange={handleChange}
                                        className="w-full bg-white border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                                    >
                                        <option value="" disabled>
                                            Select your city
                                        </option>
                                        {CITIES.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <Field
                                    label="Email (for order updates)"
                                    name="email"
                                    testid="modal-input-email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    onBlur={() => fbAdvancedMatch({ email: form.email, phone: form.phone })}
                                    placeholder="you@example.com"
                                    className="sm:col-span-2"
                                />
                                <Field
                                    label="Delivery Notes (Optional)"
                                    name="notes"
                                    testid="modal-input-notes"
                                    value={form.notes}
                                    onChange={handleChange}
                                    placeholder="e.g. Call before delivery"
                                />
                            </div>

                            <label className="flex items-start gap-3 bg-forest-50/60 border border-forest-100 rounded-xl px-4 py-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="marketing-opt-in"
                                    checked={marketingOptIn}
                                    onChange={(e) =>
                                        setMarketingOptIn(e.target.checked)
                                    }
                                    className="mt-0.5 w-4 h-4 accent-forest-700"
                                />
                                <span className="text-xs text-forest-900/80 leading-snug">
                                    Email me Resham Drops updates, hair tips
                                    and member-only offers. Unsubscribe anytime
                                    from the footer of any email.
                                </span>
                            </label>
                        </div>

                        {/* Step 3: Payment */}
                        <div className="space-y-3">
                            <h3 className="font-serif font-bold text-base text-forest-800 flex items-center gap-2.5 border-b border-forest-100 pb-2.5">
                                <span className="w-6 h-6 rounded-full bg-forest-800 text-gold-400 text-[10px] flex items-center justify-center font-sans font-bold">
                                    3
                                </span>
                                Payment method
                            </h3>

                            <OnlinePayBanner testid="modal-online-pay-banner" />

                            <div className="grid grid-cols-2 gap-3">
                                <PaymentOption
                                    value="cod"
                                    label="Cash on Delivery"
                                    sub="Pay cash at the door"
                                    icon={BanknoteArrowUp}
                                    testid="payment-cod"
                                    current={paymentMethod}
                                    onChange={setPaymentMethod}
                                />
                                {settings?.online_payment_enabled && (
                                    <>
                                        <PaymentOption
                                            value="easypaisa"
                                            label="Easypaisa"
                                            sub="Mobile wallet"
                                            icon={CreditCard}
                                            testid="payment-easypaisa"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                        <PaymentOption
                                            value="jazzcash"
                                            label="JazzCash"
                                            sub="Mobile wallet"
                                            icon={CreditCard}
                                            testid="payment-jazzcash"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                        <PaymentOption
                                            value="hbl"
                                            label="HBL Bank Transfer"
                                            sub="IBAN / account transfer"
                                            icon={CreditCard}
                                            testid="payment-hbl"
                                            badge={`Save ${settings.online_payment_discount}`}
                                            current={paymentMethod}
                                            onChange={setPaymentMethod}
                                        />
                                    </>
                                )}
                            </div>

                            {isOnline && settings?.payment_methods && (
                                <div
                                    data-testid="online-payment-panel"
                                    className="rounded-2xl bg-white border border-forest-100 p-4 space-y-3"
                                >
                                    <PaymentDetails
                                        method={paymentMethod}
                                        details={settings.payment_methods[paymentMethod]}
                                    />

                                    <label className="block">
                                        <span className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                                            Upload payment screenshot *
                                        </span>
                                        <div className="flex items-center gap-3">
                                            <label
                                                htmlFor="payment-screenshot-input"
                                                className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 text-gold-400 text-xs font-bold px-4 py-2.5 rounded-xl cursor-pointer border border-gold-500/40"
                                            >
                                                {uploading ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                ) : (
                                                    <Upload className="w-3.5 h-3.5" />
                                                )}
                                                {screenshotUrl ? "Replace screenshot" : "Upload screenshot"}
                                            </label>
                                            <input
                                                id="payment-screenshot-input"
                                                data-testid="payment-screenshot-input"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={uploading}
                                                onChange={handleScreenshotUpload}
                                            />
                                            {screenshotUrl && (
                                                <span className="inline-flex items-center gap-1 text-xs text-forest-700 font-semibold">
                                                    <ImageIcon className="w-3.5 h-3.5" />
                                                    Uploaded
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-forest-700/70 mt-1 block">
                                            JPG/PNG/WEBP, up to 5 MB. Required to confirm payment.
                                        </span>
                                    </label>
                                </div>
                            )}

                            <div className="bg-white p-4 rounded-xl border border-forest-100 space-y-1.5">
                                <SummaryRow
                                    label="Package"
                                    value={selected?.label}
                                />
                                <SummaryRow
                                    label="Subtotal"
                                    value={`${selected?.price?.toLocaleString()} PKR`}
                                />
                                {validCode && (
                                    <SummaryRow
                                        label={`Discount (${discountPct}%)`}
                                        value={`−${discountAmount.toLocaleString()} PKR`}
                                        valueClass="text-gold-700 font-bold"
                                    />
                                )}
                                {onlineEligible && (
                                    <SummaryRow
                                        label="Online payment bonus"
                                        value={`−${onlineDiscountAmount.toLocaleString()} PKR`}
                                        valueClass="text-forest-700 font-bold"
                                    />
                                )}
                                <SummaryRow
                                    label="Delivery"
                                    value={deliveryFee > 0 ? `${deliveryFee.toLocaleString()} PKR` : "FREE"}
                                    valueClass={deliveryFee > 0 ? "" : "text-forest-700 font-bold"}
                                />
                                <div className="border-t border-forest-100 pt-2 flex justify-between items-baseline">
                                    <span className="text-sm font-bold text-forest-900">
                                        Total to pay
                                    </span>
                                    <span
                                        data-testid="modal-order-total"
                                        className="text-2xl font-extrabold text-forest-800 font-serif"
                                    >
                                        {payableAmount.toLocaleString()} PKR
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="modal-submit-order"
                            disabled={loading}
                            className="w-full bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-white font-bold text-base sm:text-lg py-4 rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3 border-2 border-gold-500 sticky bottom-0"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin text-gold-400" />
                                    Placing your order...
                                </>
                            ) : (
                                <>
                                    <CircleCheck className="w-6 h-6 text-gold-400 fill-forest-700" />
                                    {paymentMethod === "cod"
                                        ? "CONFIRM COD ORDER"
                                        : "CONFIRM ONLINE ORDER"}
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>

            <OrderSuccessModal order={order} onClose={closeAll} />
        </>,
        document.body,
    );
}

function Field({ label, required, hint, className = "", testid, ...props }) {
    return (
        <div className={className}>
            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                {label} {required && "*"}
            </label>
            <input
                data-testid={testid}
                required={required}
                className="w-full bg-white border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                {...props}
            />
            {hint && (
                <span className="text-[11px] text-forest-700/70 mt-1 block">
                    {hint}
                </span>
            )}
        </div>
    );
}

function SummaryRow({ label, value, valueClass = "" }) {
    return (
        <div className="flex justify-between text-xs text-forest-900/70">
            <span>{label}:</span>
            <span className={`font-semibold text-forest-900 ${valueClass}`}>
                {value}
            </span>
        </div>
    );
}

function PaymentOption({ value, label, sub, icon: Icon, testid, badge, current, onChange }) {
    const active = current === value;
    return (
        <label
            data-testid={testid}
            className={`cursor-pointer rounded-2xl p-3 border-2 transition-all relative ${active ? "border-forest-700 bg-white shadow" : "border-forest-100 bg-white hover:border-forest-400"}`}
        >
            {badge && (
                <span className="absolute -top-2.5 right-3 bg-gold-500 text-forest-900 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                    {badge}
                </span>
            )}
            <input
                type="radio"
                name="payment"
                checked={active}
                onChange={() => onChange(value)}
                className="sr-only"
            />
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="font-bold text-forest-900 text-sm leading-tight truncate">
                        {label}
                    </div>
                    <div className="text-[11px] text-forest-700/80 truncate">
                        {sub}
                    </div>
                </div>
            </div>
        </label>
    );
}

function PaymentDetails({ method, details }) {
    if (!details) return null;
    const copy = (val) => {
        try {
            navigator.clipboard.writeText(val);
            toast.success("Copied");
        } catch {
            /* ignore */
        }
    };
    const row = (k, v) => (
        <button
            type="button"
            key={k}
            onClick={() => copy(v)}
            className="w-full flex items-center justify-between bg-forest-50 hover:bg-forest-100 border border-forest-100 rounded-lg px-3 py-2 text-left transition-colors"
        >
            <span className="text-[10px] font-bold uppercase tracking-widest text-forest-700">
                {k}
            </span>
            <span className="font-mono text-xs text-forest-900 font-bold truncate ml-3">
                {v}
            </span>
        </button>
    );
    return (
        <div
            data-testid={`payment-details-${method}`}
            className="bg-forest-900 text-cream rounded-xl p-4 space-y-2 border border-gold-500/30"
        >
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gold-400 uppercase tracking-widest">
                    {details.label || method}
                </span>
                <span className="text-[10px] text-cream/60">Tap to copy</span>
            </div>
            <div className="space-y-1.5">
                {details.title && row("Account Title", details.title)}
                {details.number && row("Account Number", details.number)}
                {details.bank && row("Bank", details.bank)}
                {details.iban && row("IBAN", details.iban)}
            </div>
            <p className="text-[11px] text-cream/70 pt-1">
                Send the exact total shown below, then upload a screenshot of your transfer / wallet payment to confirm.
            </p>
        </div>
    );
}
